import type { ReactNode } from "react";

import { cn } from "@/lib/cn";

/**
 * A machine identity rendered so that a right-to-left document cannot reorder
 * it (#640).
 *
 * ## Why this is not a `<span className="font-mono">`
 *
 * Everything this surface renders as an identity is a technical string with
 * neutral characters at its edges and inside it: route templates
 * (`/v1/library/assets/{assetId}/versions`), the placement directory template
 * (`summary_agent/{lang}/per_key/{slug}`), dotted address segments, asset ids,
 * version labels and a sha256 digest. Under `dir="rtl"` the Unicode
 * bidirectional algorithm resolves those leading and trailing slashes and
 * braces to the paragraph direction, and the string is DISPLAYED with its
 * segments reversed — a reader in a Hebrew locale is shown a route that does
 * not exist. It is not a styling nicety: the rendered path is wrong.
 *
 * `dir="ltr"` on the element pins the embedding level for the whole string, so
 * the identity reads identically in both directions. The application has no
 * locale mechanism yet (`index.html` is `lang="en"`; direction belongs to #455
 * and #10), which is exactly why the surfaces built now must be direction-
 * agnostic — the same bounded RTL scope `shell/SectionRail.tsx` carries.
 *
 * `unicode-bidi: isolate` on top of the direction is what keeps the identity
 * from disturbing the direction of the text AROUND it: without isolation a
 * left-to-right run embedded in a right-to-left sentence pulls neighbouring
 * neutrals — the comma and full stop after it — to the wrong side.
 */
export function Identity({
  children,
  className,
  title,
}: {
  children: ReactNode;
  className?: string;
  /** The full value, when the visible text is an abbreviation of it. */
  title?: string;
}) {
  return (
    <span
      dir="ltr"
      className={cn(
        "wrap-anywhere [unicode-bidi:isolate] font-mono text-xs text-foreground",
        className,
      )}
      {...(title === undefined ? {} : { title })}
    >
      {children}
    </span>
  );
}

/**
 * Text whose direction is decided by its own first strong character.
 *
 * The counterpart to `Identity`, for the one class of string on this surface
 * that is NOT a machine identity: content a person wrote. A Hebrew library
 * asset name inside a left-to-right paragraph, or an English one inside a
 * Hebrew paragraph, is mixed-direction content, and `dir="auto"` is the only
 * treatment that gets both right without the browser being told which it is —
 * the same reason `ui/publication/CandidateReview.tsx` renders frozen artifact
 * bodies with it.
 *
 * Isolated for `Identity`'s reason: a right-to-left name embedded in a
 * left-to-right sentence must not drag the sentence's punctuation with it.
 */
export function AuthoredText({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      dir="auto"
      className={cn("wrap-anywhere [unicode-bidi:isolate]", className)}
    >
      {children}
    </span>
  );
}
